import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const FeaturesPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Enterprise-Grade Features
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Built for reliability, compliance, and seamless integration with your existing systems.
            </p>
          </div>
        </section>

        {/* Core Features */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Core Capabilities
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Everything you need to handle high call volumes with confidence.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-24-hours-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">24/7 Availability</h3>
                <p className="text-gray-600">
                  Never miss a call, even during nights, weekends, and holidays. Your AI agent is always on.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-user-search-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Smart Qualification</h3>
                <p className="text-gray-600">
                  Ask the right questions to qualify leads before routing to your team. Filter spam and tire-kickers automatically.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-calendar-check-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Appointment Booking</h3>
                <p className="text-gray-600">
                  Schedule directly into your calendar with conflict checking and automated confirmations.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-phone-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Intelligent Routing</h3>
                <p className="text-gray-600">
                  Transfer hot leads to the right team member based on availability, expertise, and priority.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-file-text-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">Call Transcripts</h3>
                <p className="text-gray-600">
                  Every call is transcribed and logged with key details, outcomes, and next steps.
                </p>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-teal-light rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-database-2-line text-3xl text-teal"></i>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-3">CRM Integration</h3>
                <p className="text-gray-600">
                  Automatically create leads, log notes, and update records in your existing CRM system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Reliability & Safety */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Reliability & Safety
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Built for CFOs and Managing Partners who need assurance, not promises.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">No Guessing Policy</h3>
                <p className="text-sm text-gray-600">
                  If confidence is low, the agent stops and hands off to your team. No improvisation.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-route-line text-2xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Failover Routing</h3>
                <p className="text-sm text-gray-600">
                  If AI or carrier issues occur, calls route to your team, answering service, or voicemail with SMS/email alerts.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-line-chart-line text-2xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Monitoring & Alerts</h3>
                <p className="text-sm text-gray-600">
                  Track containment, transfers, and drops. Get alerted on anomalies and call volume spikes.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-checkbox-circle-line text-2xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">QA & Testing</h3>
                <p className="text-sm text-gray-600">
                  Pre-launch stress tests plus weekly call reviews from transcripts and recordings.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-2xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Full Visibility</h3>
                <p className="text-sm text-gray-600">
                  Every call includes recording, transcript, and disposition. Complete audit trail.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-lock-line text-2xl text-teal"></i>
                </div>
                <h3 className="text-lg font-semibold text-navy mb-3">Compliance Guardrails</h3>
                <p className="text-sm text-gray-600">
                  Follows approved scripts. Intake and scheduling only—no advice, quotes, or commitments.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Integration */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">
                Works with Your Stack
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Seamless integration with the tools you already use. No rip-and-replace required.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12 border-2 border-gray-200">
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div>
                  <h3 className="text-xl font-semibold text-navy mb-6">Phone Systems</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">RingCentral</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Twilio</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Vonage</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">8x8</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-navy mb-6">CRM Systems</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Salesforce</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">HubSpot</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Clio (Legal)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Zoho CRM</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-navy mb-6">Calendars</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Google Calendar</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Microsoft 365</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Calendly</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Acuity Scheduling</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-navy mb-6">Communication</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Slack</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Microsoft Teams</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">Email (SMTP)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-teal rounded-full"></div>
                      <span className="text-gray-700">SMS Notifications</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-teal-light rounded-xl p-6 text-center">
                <p className="text-navy font-medium">
                  Don't see your system? We offer custom integrations via API and webhooks.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Analytics */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6">
                  Analytics & Insights
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Understand your call patterns, conversion rates, and agent performance with detailed analytics.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Call volume tracking</strong>
                      <p className="text-gray-600 text-sm">Monitor daily, weekly, and monthly call patterns</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Conversion metrics</strong>
                      <p className="text-gray-600 text-sm">Track qualification rates and booking success</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Response time analysis</strong>
                      <p className="text-gray-600 text-sm">Measure how quickly calls are answered</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-navy">Revenue attribution</strong>
                      <p className="text-gray-600 text-sm">Connect calls to closed deals and revenue</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Calls Answered</span>
                      <span className="text-2xl font-bold text-teal">94%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-teal h-3 rounded-full" style={{ width: '94%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Lead Qualification Rate</span>
                      <span className="text-2xl font-bold text-teal">78%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-teal h-3 rounded-full" style={{ width: '78%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Appointments Booked</span>
                      <span className="text-2xl font-bold text-teal">86%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-teal h-3 rounded-full" style={{ width: '86%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Caller Satisfaction</span>
                      <span className="text-2xl font-bold text-teal">92%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-teal h-3 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              See PointCalls in Action
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Experience the features firsthand with a personalized demo.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/contact" 
                className="px-10 py-4 bg-teal text-white font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
              >
                Schedule a Demo
              </Link>
              <a 
                href="tel:+16787376473" 
                className="px-10 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-200 text-lg whitespace-nowrap cursor-pointer"
              >
                Call Live Demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FeaturesPage;